import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    User as FirebaseUser,
    GoogleAuthProvider,
    signInWithPopup,
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential,
    sendPasswordResetEmail,
    sendEmailVerification
} from 'firebase/auth';
import {
    doc,
    setDoc,
    getDoc,
    updateDoc,
    onSnapshot,
    serverTimestamp,
    writeBatch,
    getDocs,
    collection,
    query,
    where,
    deleteField
} from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { auth, db, storage, functions } from './firebase';
import { User, AppLanguage } from '../types';
import { getUTMParams, getConnectionMetadata, fetchIpInfo } from '../utils/saas';
import { formatFirstName, formatLastName, formatFullName } from '../utils';

// Convertir Firebase User en App User
const mapFirebaseUserToUser = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    const splitName = (firebaseUser.displayName || '').split(' ');
    const firstNameAuth = splitName[0] || 'User';
    const lastNameAuth = splitName.slice(1).join(' ') || '';

    const fallbackUser: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        firstName: formatFirstName(firstNameAuth),
        lastName: formatLastName(lastNameAuth),
        pseudo: `user_${firebaseUser.uid.substring(0, 6)}`,
        displayName: formatFullName(firstNameAuth, lastNameAuth),
        photoURL: firebaseUser.photoURL || undefined,
        role: 'USER',
        status: 'ACTIVE',
        subscriptionTier: 'FREE',
        subscriptionPeriod: 'MONTHLY',
        subscriptionStatus: 'ACTIVE',
        tradesCount: 0,
        auditsUsedThisMonth: 0,
        fundamentalAnalysesUsedThisMonth: 0,
        defaultCurrency: 'USD',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        metadata: {
            registration: {
                acceptedTermsVersion: '1.0',
                acceptedTermsAt: new Date(),
            },
            lastConnection: {},
            usage: {
                lastActiveAt: new Date()
            }
        }
    };

    try {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
                ...fallbackUser,
                ...userData,
                id: firebaseUser.uid,
                email: firebaseUser.email || userData.email || '',
                preferredLanguage: userData.preferredLanguage || (localStorage.getItem('starquant_lang') as AppLanguage) || 'fr',
                createdAt: userData.createdAt?.toDate() || new Date(),
                lastLoginAt: userData.lastLoginAt?.toDate() || new Date(),
                subscriptionEndDate: userData.subscriptionEndDate?.toDate() || undefined,
                metadata: {
                    ...fallbackUser.metadata,
                    ...(userData.metadata || {}),
                    registration: {
                        ...fallbackUser.metadata.registration,
                        ...(userData.metadata?.registration || {}),
                        acceptedTermsAt: userData.metadata?.registration?.acceptedTermsAt?.toDate() || fallbackUser.metadata.registration.acceptedTermsAt
                    },
                    usage: {
                        ...fallbackUser.metadata.usage,
                        ...(userData.metadata?.usage || {}),
                        lastActiveAt: userData.metadata?.usage?.lastActiveAt?.toDate() || fallbackUser.metadata.usage.lastActiveAt
                    }
                }
            } as User;
        }
        return fallbackUser;
    } catch (error) {
        console.error('Error mapping user:', error);
        return fallbackUser;
    }
};

// Inscription
export const registerUser = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    pseudo: string,
    traderProfile: User['traderProfile'] = 'DAY_TRADER',
    preferredLanguage: AppLanguage = 'fr'
): Promise<User> => {
    try {
        const utms = getUTMParams();
        const connection = getConnectionMetadata();
        const ipInfo = await fetchIpInfo();

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await updateProfile(user, {
            displayName: formatFullName(firstName, lastName)
        });

        const newUser: Omit<User, 'id'> = {
            email: email,
            firstName,
            lastName: formatLastName(lastName),
            pseudo: pseudo.trim(),
            displayName: formatFullName(firstName, lastName),
            role: 'USER',
            status: 'ACTIVE',
            isAdminChecked: false,
            traderProfile,
            preferredLanguage,
            subscriptionTier: 'FREE',
            subscriptionPeriod: 'MONTHLY',
            subscriptionStatus: 'ACTIVE',
            tradesCount: 0,
            auditsUsedThisMonth: 0,
            fundamentalAnalysesUsedThisMonth: 0,
            defaultCurrency: 'USD',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            hasSeenTutorial: false,
            createdAt: new Date(),
            lastLoginAt: new Date(),
            metadata: {
                registration: {
                    utm_source: utms.utm_source || 'direct',
                    utm_medium: utms.utm_medium || 'none',
                    utm_campaign: utms.utm_campaign || 'none',
                    acceptedTermsVersion: '1.0',
                    acceptedTermsAt: new Date(),
                    userAgent: connection.userAgent || 'unknown',
                    ip: ipInfo.ip || 'unknown'
                },
                lastConnection: {
                    ...connection,
                    ip: ipInfo.ip,
                    location: ipInfo.location
                },
                usage: {
                    lastActiveAt: new Date()
                }
            }
        };

        // Créer l'objet final pour Firestore avec des valeurs sécurisées
        const finalUserDoc = {
            ...newUser,
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
            metadata: {
                ...newUser.metadata,
                registration: {
                    ...newUser.metadata.registration,
                    acceptedTermsAt: serverTimestamp()
                },
                usage: {
                    ...newUser.metadata.usage,
                    lastActiveAt: serverTimestamp()
                }
            }
        };

        await setDoc(doc(db, 'users', user.uid), finalUserDoc);

        return { id: user.uid, ...newUser };
    } catch (error: any) {
        console.error('Registration error:', error);
        throw new Error(error.message || 'Erreur lors de l\'inscription');
    }
};

// Connexion
export const loginUser = async (email: string, password: string): Promise<User> => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const connection = getConnectionMetadata();
        const ipInfo = await fetchIpInfo();

        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
            lastLoginAt: serverTimestamp(),
            'metadata.lastConnection': {
                ...connection,
                ip: ipInfo.ip,
                location: ipInfo.location
            },
            'metadata.usage.lastActiveAt': serverTimestamp()
        });

        const appUser = await mapFirebaseUserToUser(user);
        if (!appUser) throw new Error('Utilisateur non trouvé en base de données');
        return appUser;
    } catch (error: any) {
        console.error('Login error:', error);
        throw new Error(error.message || 'Erreur lors de la connexion');
    }
};

// Connexion Google
export const loginWithGoogle = async (traderProfile?: User['traderProfile'], preferredLanguage?: AppLanguage): Promise<User> => {
    try {
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        const user = userCredential.user;

        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            const splitName = (user.displayName || '').split(' ');
            const firstName = formatFirstName(splitName[0] || 'User');
            const lastName = formatLastName(splitName.slice(1).join(' ') || '');
            const pseudo = `user_${user.uid.substring(0, 6)}`;

            const connection = getConnectionMetadata();
            const ipInfo = await fetchIpInfo();
            const utms = getUTMParams();

            const newUser: Omit<User, 'id'> = {
                email: user.email || '',
                firstName,
                lastName,
                pseudo,
                displayName: formatFullName(firstName, lastName),
                photoURL: user.photoURL || undefined,
                role: 'USER',
                status: 'ACTIVE',
                isAdminChecked: false,
                subscriptionTier: 'FREE',
                subscriptionPeriod: 'MONTHLY',
                subscriptionStatus: 'ACTIVE',
                traderProfile: traderProfile || 'DAY_TRADER',
                preferredLanguage: preferredLanguage || (localStorage.getItem('starquant_lang') as AppLanguage) || 'fr',
                tradesCount: 0,
                auditsUsedThisMonth: 0,
                fundamentalAnalysesUsedThisMonth: 0,
                defaultCurrency: 'USD',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                hasSeenTutorial: false,
                createdAt: new Date(),
                lastLoginAt: new Date(),
                metadata: {
                    registration: {
                        utm_source: utms.utm_source || 'direct',
                        utm_medium: utms.utm_medium || 'none',
                        utm_campaign: utms.utm_campaign || 'none',
                        acceptedTermsVersion: '1.0',
                        acceptedTermsAt: new Date(),
                        userAgent: connection.userAgent || 'unknown',
                        ip: ipInfo.ip || 'unknown'
                    },
                    lastConnection: {
                        ...connection,
                        ip: ipInfo.ip,
                        location: ipInfo.location
                    },
                    usage: {
                        lastActiveAt: new Date()
                    }
                }
            };

            // Créer l'objet final pour Firestore avec des valeurs sécurisées
            const finalUserDoc = {
                ...newUser,
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp(),
                metadata: {
                    ...newUser.metadata!,
                    registration: {
                        ...newUser.metadata!.registration,
                        acceptedTermsAt: serverTimestamp()
                    },
                    usage: {
                        ...newUser.metadata!.usage,
                        lastActiveAt: serverTimestamp()
                    }
                }
            };

            await setDoc(userDocRef, finalUserDoc);

            return { id: user.uid, ...newUser };
        } else {
            const connection = getConnectionMetadata();
            const ipInfo = await fetchIpInfo();

            await updateDoc(userDocRef, {
                lastLoginAt: serverTimestamp(),
                'metadata.lastConnection': {
                    ...connection,
                    ip: ipInfo.ip,
                    location: ipInfo.location
                },
                'metadata.usage.lastActiveAt': serverTimestamp()
            });
            const appUser = await mapFirebaseUserToUser(user);
            return appUser!;
        }
    } catch (error: any) {
        console.error('Google login error:', error);
        throw new Error(error.message || 'Erreur lors de la connexion avec Google');
    }
};

// Déconnexion
export const logoutUser = async (): Promise<void> => {
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Logout error:', error);
        throw error;
    }
};

// Mettre à jour le profil utilisateur
export const updateUserProfile = async (
    userId: string,
    data: Partial<Pick<User, 'firstName' | 'lastName' | 'pseudo' | 'photoURL' | 'traderProfile' | 'preferredLanguage'>>
): Promise<void> => {
    try {
        const userDocRef = doc(db, 'users', userId);
        const updateData: any = {
            updatedAt: serverTimestamp(),
            isAdminChecked: false
        };

        // On ne copie que les valeurs définies pour éviter l'erreur Firestore undefined
        Object.entries(data).forEach(([key, value]) => {
            if (value !== undefined) {
                updateData[key] = value;
            } else {
                updateData[key] = deleteField(); // Utiliser deleteField() pour supprimer proprement si undefined
            }
        });

        await updateDoc(userDocRef, updateData);

        // --- Synchronisation avec les stratégies ---
        // Si le nom, prénom ou pseudo change, on doit mettre à jour les stratégies denormalisées
        if (data.firstName || data.lastName || data.pseudo) {
            const strategiesQ = query(collection(db, 'strategies'), where('userId', '==', userId));
            const strategiesSnap = await getDocs(strategiesQ);

            if (!strategiesSnap.empty) {
                const batch = writeBatch(db);
                strategiesSnap.docs.forEach(strategyDoc => {
                    const strategyUpdate: any = {};
                    if (data.firstName) strategyUpdate.authorFirstName = data.firstName;
                    if (data.lastName) strategyUpdate.authorLastName = data.lastName;
                    if (data.pseudo) strategyUpdate.authorPseudo = data.pseudo;
                    if (data.firstName || data.lastName) {
                        const currentData = strategyDoc.data();
                        strategyUpdate.authorName = `${data.firstName || currentData.authorFirstName} ${data.lastName || currentData.authorLastName}`;
                    }
                    batch.update(strategyDoc.ref, strategyUpdate);
                });
                await batch.commit();
                console.log(`Synchronisation de ${strategiesSnap.size} stratégies réussie.`);
            }
        }

        const currentUser = auth.currentUser;
        if (currentUser && (data.firstName || data.lastName || data.photoURL)) {
            const updates: any = {};
            if (data.firstName || data.lastName) {
                const userDoc = await getDoc(userDocRef);
                const fullData = userDoc.data();
                updates.displayName = formatFullName(data.firstName || fullData?.firstName, data.lastName || fullData?.lastName);
            }
            if (data.photoURL !== undefined) updates.photoURL = data.photoURL;
            await updateProfile(currentUser, updates);
        }
    } catch (error: any) {
        console.error('Update profile error:', error);
        throw new Error(error.message || 'Erreur lors de la mise à jour du profil');
    }
};

// Marquer le didacticiel comme vu
export const markTutorialAsSeen = async (userId: string): Promise<void> => {
    try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
            hasSeenTutorial: true,
            updatedAt: serverTimestamp()
        });
    } catch (error: any) {
        console.error('Error marking tutorial as seen:', error);
    }
};

// Envoyer un email de vérification
export const sendVerificationEmail = async (): Promise<void> => {
    try {
        if (auth.currentUser) await sendEmailVerification(auth.currentUser);
    } catch (error: any) {
        console.error('Email verification error:', error);
        throw new Error(error.message || 'Erreur lors de l\'envoi de l\'email de vérification');
    }
};

// --- FONCTION INTERNE DE NETTOYAGE STORAGE ---
const cleanupUserStorage = async (userId: string) => {
    // Nettoyage récursif du dossier utilisateur dans Storage
    // (/users/{userId}/**)
    const deleteFolderRecursive = async (path: string) => {
        try {
            const folderRef = ref(storage, path);
            const listRes = await listAll(folderRef);

            // Supprimer tous les fichiers du dossier actuel
            const deleteFilesPromises = listRes.items.map(item => deleteObject(item));
            await Promise.all(deleteFilesPromises);

            // Supprimer récursivement les sous-dossiers
            for (const prefix of listRes.prefixes) {
                await deleteFolderRecursive(prefix.fullPath);
            }
        } catch (e) {
            console.warn(`Storage cleanup failed for path ${path}:`, e);
        }
    };

    await deleteFolderRecursive(`users/${userId}`);
};

// Suppression de compte (Par l'utilisateur lui-même)
export const deleteUserAccount = async (userId: string): Promise<void> => {
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
        throw new Error("Authentification requise.");
    }

    try {
        console.log("Début de la procédure de suppression de compte pour :", userId);

        // 1. Nettoyer uniquement le STORAGE (car nécessite une itération côté client ou règles spécifiques)
        // On le fait tant que l'utilisateur est connecté.
        await cleanupUserStorage(userId);
        console.log("Nettoyage Storage réussi.");

        // 2. Supprimer tout le reste (Firestore + Auth) via Cloud Function
        // Cette fonction contourne les Restricted Rules et gère les votes tiers.
        const deleteAuth = httpsCallable(functions, 'adminDeleteUserAccount');
        await deleteAuth({ uid: userId });

        console.log("Compte et données supprimés via Cloud Function.");
    } catch (error: any) {
        console.error('Account deletion error:', error);
        throw new Error(error.message || 'Erreur lors de la suppression du compte');
    }
};

// Suppression de compte (Par un Admin)
export const adminDeleteUser = async (userId: string): Promise<void> => {
    try {
        // 1. Nettoyer le Storage
        await cleanupUserStorage(userId);

        // 2. Cloud Function pour tout le reste (Firestore + Auth)
        const adminDeleteAuth = httpsCallable(functions, 'adminDeleteUserAccount');
        await adminDeleteAuth({ uid: userId });

        console.log(`Suppression administrative complète pour : ${userId}`);
    } catch (error: any) {
        console.error('Admin deletion error:', error);
        throw new Error(error.message || 'Erreur suppression administrative');
    }
};

// Ban
export const toggleUserBan = async (userId: string, shouldBan: boolean): Promise<void> => {
    try {
        const batch = writeBatch(db);
        const status = shouldBan ? 'BANNED' : 'ACTIVE';
        batch.update(doc(db, 'users', userId), { status, updatedAt: serverTimestamp() });

        const strategiesQ = query(collection(db, 'strategies'), where('userId', '==', userId));
        const strategiesSnap = await getDocs(strategiesQ);
        strategiesSnap.docs.forEach(d => batch.update(d.ref, { authorStatus: status, updatedAt: serverTimestamp() }));

        await batch.commit();
    } catch (error: any) {
        console.error('Ban toggle error:', error);
        throw new Error(error.message || "Erreur statut utilisateur.");
    }
};

// Password
export const updateUserPassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("Non authentifié.");
    try {
        const credential = EmailAuthProvider.credential(user.email, oldPassword);
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, newPassword);
    } catch (error: any) {
        console.error('Password update error:', error);
        if (error.code === 'auth/wrong-password') throw new Error("Ancien mot de passe incorrect.");
        throw new Error(error.message || "Erreur mise à jour mot de passe.");
    }
};

// Reset Password
export const resetPassword = async (email: string): Promise<void> => {
    try {
        await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
        console.error('Reset password error:', error);
        throw new Error(error.message || "Erreur envoi email.");
    }
};

// Auth listener temps-réel avec synchronisation Stripe
// Auth listener temps-réel avec synchronisation Stripe (Optimisé contre le flickering)
export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
    let unsubscribeUser: (() => void) | null = null;
    let unsubscribeSubs: (() => void) | null = null;

    return onAuthStateChanged(auth, async (firebaseUser) => {
        if (unsubscribeUser) unsubscribeUser();
        if (unsubscribeSubs) unsubscribeSubs();

        if (firebaseUser) {
            console.log("🔐 Auth détectée pour :", firebaseUser.email);
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            const subsRef = collection(db, 'customers', firebaseUser.uid, 'subscriptions');

            let currentBaseUser: User | null = null;
            let currentStripeSub: any = null;

            const emitMergedUser = () => {
                if (!currentBaseUser) return;

                let finalUser = { ...currentBaseUser };

                if (currentStripeSub) {
                    const priceId = currentStripeSub.items?.[0]?.price?.id;
                    let tier: User['subscriptionTier'] = 'FREE';

                    if (['price_1So8R1IpV7ED4Y44ESzV2kZx', 'price_1So8R1IpV7ED4Y44HU12moaj'].includes(priceId)) {
                        tier = 'ADVANCED';
                    } else if (['price_1So8WsIpV7ED4Y44zfvl3Hdq', 'price_1So8WsIpV7ED4Y44v2UEDq1m'].includes(priceId)) {
                        tier = 'PROFESSIONAL';
                    }

                    finalUser = {
                        ...finalUser,
                        subscriptionTier: tier,
                        subscriptionStatus: 'ACTIVE',
                        subscriptionPeriod: currentStripeSub.items?.[0]?.plan?.interval === 'year' ? 'YEARLY' : 'MONTHLY'
                    };
                    console.log(`✅ Tier synchronisé via Stripe : ${tier}`);
                } else {
                    console.log(`ℹ️ Aucun abonnement Stripe actif trouvé, utilisation du tier Firestore : ${finalUser.subscriptionTier}`);

                    // Vérification de l'expiration du plan sans Stripe (Trial 30 jours, etc.)
                    if (finalUser.subscriptionTier !== 'FREE' && finalUser.subscriptionEndDate) {
                        const now = new Date();
                        if (finalUser.subscriptionEndDate < now) {
                            console.log(`⚠️ Abonnement ou Essai expiré. Rétrogradation automatique vers FREE.`);
                            finalUser.subscriptionTier = 'FREE';
                            finalUser.subscriptionStatus = 'EXPIRED';

                            // Mise à jour de Firestore pour persister la rétrogradation
                            updateDoc(doc(db, 'users', finalUser.id), {
                                subscriptionTier: 'FREE',
                                subscriptionStatus: 'EXPIRED'
                            }).catch(err => console.error("Erreur lors de la rétrogradation:", err));
                        }
                    }
                }

                callback(finalUser);
            };

            // 1. Écoute du document utilisateur
            unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    const userData = docSnap.data();
                    currentBaseUser = {
                        id: firebaseUser.uid,
                        email: firebaseUser.email || userData.email || '',
                        ...userData,
                        createdAt: userData.createdAt?.toDate() || new Date(),
                        lastLoginAt: userData.lastLoginAt?.toDate() || new Date(),
                        subscriptionEndDate: userData.subscriptionEndDate?.toDate() || undefined,
                    } as User;
                    emitMergedUser();
                } else {
                    mapFirebaseUserToUser(firebaseUser).then(fallback => {
                        currentBaseUser = fallback;
                        emitMergedUser();
                    });
                }
            });

            // 2. Écoute des abonnements Stripe
            const subsQuery = query(subsRef, where('status', 'in', ['active', 'trialing']));
            unsubscribeSubs = onSnapshot(subsQuery, (subsSnap) => {
                if (!subsSnap.empty) {
                    currentStripeSub = subsSnap.docs[0].data();
                } else {
                    currentStripeSub = null;
                }
                emitMergedUser();
            });

        } else {
            console.log("👋 Utilisateur déconnecté");
            callback(null);
        }
    });
};
